import { cn } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  FilePdf as FilePdfIcon,
  User as UserIcon,
  UsersThree as UsersThreeIcon,
} from "@phosphor-icons/react";
import { Fragment } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import {
  stickyHeadFirstRowClass,
  stickyHeadSecondRowClass,
  stickyRowHeaderContentClass,
  useStickyHeaderOffsets,
} from "@/lib/utils/sticky-table-header";
import type { PendingDispatchItem } from "./pending-dispatch-screen";

export type PiecesAReunirTableProps = {
  loading: boolean;
  items: PendingDispatchItem[];
  emptyLabel: string;
};

// Keep the generated PDF alive long enough for the new tab to load it.
const OBJECT_URL_LIFETIME_MS = 60_000;

const headClass =
  "truncate bg-zinc-50 px-2 py-2 text-center font-medium text-zinc-700 dark:bg-zinc-900 dark:text-zinc-300";
const cellClass =
  "h-10 truncate border-zinc-200 px-2 text-zinc-700 dark:border-zinc-800 dark:text-zinc-300";
const COLUMN_TITLES = [
  { key: "slip-number", title: "N°" },
  { key: "date", title: "Date" },
  { key: "exemplaires", title: "Exemplaires dédiés" },
  { key: "global", title: "Global" },
  { key: "author", title: "Auteur" },
  { key: "lot", title: "Lot" },
  { key: "document-number", title: "N°" },
  { key: "name", title: "Intitulé" },
  { key: "index", title: "Ind." },
  { key: "quantity", title: "Quantité" },
  { key: "pdf", title: "PDF" },
];
const iconButtonClass =
  "inline-flex shrink-0 items-center justify-center rounded p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-100";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-FR");
}

/**
 * Écran 021, tableau « Bordereaux à éditer et pièces à réunir » (N116) : par
 * bordereau, les exemplaires dédiés restant à mettre au courrier (un par
 * destinataire, chacun ouvrant son PDF), le PDF « Global » de tous les
 * exemplaires, et les documents à imprimer avec leur quantité de copies papier.
 */
export function PiecesAReunirTable({
  loading,
  items,
  emptyLabel,
}: PiecesAReunirTableProps) {
  const { yardId } = useParams<{ yardId: string }>();
  const { headerRef, style } = useStickyHeaderOffsets();

  async function openBordereau(slipId: number, recipientTeamId?: number) {
    if (isNil(yardId)) return;
    try {
      const { data, error } = await apiClient.GET(
        "/api/yards/{yardId}/slips/{slipId}/bordereau-pdf",
        {
          params: {
            path: { yardId, slipId },
            query: isNotNil(recipientTeamId) ? { recipientTeamId } : {},
          },
          parseAs: "blob",
        },
      );
      if (isNotNil(data) && isNil(error)) {
        const url = URL.createObjectURL(data);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_LIFETIME_MS);
        return;
      }
    } catch (error) {
      console.error("Failed to open bordereau PDF", error);
    }
    toaster.error({
      title: "Erreur",
      description: "Impossible d'ouvrir le bordereau",
    });
  }

  async function openDocumentPdf(fichierId: number) {
    if (isNil(yardId)) return;
    try {
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        { params: { path: { yardId, fichierId: String(fichierId) } } },
      );
      if (isNotNil(data)) {
        window.open(data.data.inlineUrl, "_blank");
        return;
      }
    } catch (error) {
      console.error("Failed to open document PDF", error);
    }
    toaster.error({
      title: "Erreur",
      description: "Impossible d'ouvrir le PDF du document",
    });
  }

  return (
    <div className="flex h-full flex-col gap-2">
      <h3 className="shrink-0 text-sm font-semibold uppercase text-zinc-700 dark:text-zinc-300">
        Bordereaux à éditer et pièces à réunir
      </h3>
      <div
        className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-800"
        style={style}
      >
        {/*
          Colonnes fixes en largeur fixe ; « Exemplaires dédiés », « Auteur »
          et « Lot » en pourcentage, « Intitulé » prend le reste.
        */}
        <table className="w-full table-fixed border-collapse text-sm">
          <colgroup>
            <col className="w-16" />
            <col className="w-24" />
            <col style={{ width: "16%" }} />
            <col className="w-16" />
            <col style={{ width: "11%" }} />
            <col style={{ width: "10%" }} />
            <col className="w-20" />
            <col />
            <col className="w-12" />
            <col className="w-20" />
            <col className="w-12" />
          </colgroup>
          <thead ref={headerRef}>
            <tr>
              <th
                colSpan={4}
                className={cn(stickyHeadFirstRowClass, headClass, "border-r")}
              >
                Bordereaux
              </th>
              <th
                colSpan={7}
                className={cn(stickyHeadFirstRowClass, headClass)}
              >
                Documents
              </th>
            </tr>
            <tr>
              {COLUMN_TITLES.map(({ key, title }) => (
                <th
                  key={key}
                  title={title}
                  className={cn(
                    stickyHeadSecondRowClass,
                    headClass,
                    key === "global" && "border-r",
                  )}
                >
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={11}
                  className="px-3 py-8 text-center text-zinc-500"
                >
                  Chargement...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={11}
                  className="px-3 py-12 text-center text-zinc-500"
                >
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const label = item.number ?? "-";
                const rowCount = Math.max(
                  1,
                  item.recipients.length,
                  item.documents.length,
                );
                const slipTitle = isNotNil(item.authorName)
                  ? `Bordereau n° ${label}, édité par ${item.authorName}`
                  : `Bordereau n° ${label}`;
                return (
                  <Fragment key={item.slipId}>
                    {Array.from({ length: rowCount }, (_, i) => {
                      const recipient = item.recipients.at(i);
                      const doc = item.documents.at(i);
                      const pdfId = doc?.fichierPdfId;
                      const docName = doc?.name ?? "ce document";
                      const last = i === rowCount - 1;
                      const rowCell = cn(cellClass, last && "border-b");
                      return (
                        <tr key={`${item.slipId}-${i}`}>
                          {i === 0 && (
                            <>
                              <td
                                rowSpan={rowCount}
                                className={cn(cellClass, "border-b align-top")}
                              >
                                <div
                                  className={cn(
                                    stickyRowHeaderContentClass,
                                    "flex h-10 items-center justify-center truncate",
                                  )}
                                  title={slipTitle}
                                >
                                  {label}
                                </div>
                              </td>
                              <td
                                rowSpan={rowCount}
                                className={cn(cellClass, "border-b align-top")}
                              >
                                <div
                                  className={cn(
                                    stickyRowHeaderContentClass,
                                    "flex h-10 items-center justify-center",
                                  )}
                                >
                                  {formatDate(item.createdAt)}
                                </div>
                              </td>
                            </>
                          )}
                          <td className={rowCell}>
                            {isNotNil(recipient) && (
                              <div className="flex items-center justify-between gap-1">
                                <span
                                  className="truncate"
                                  title={recipient.name}
                                >
                                  {recipient.name}
                                </span>
                                <button
                                  type="button"
                                  className={iconButtonClass}
                                  title={`Voir l'exemplaire de ${recipient.name}`}
                                  aria-label={`Voir l'exemplaire de ${recipient.name}`}
                                  onClick={() =>
                                    openBordereau(item.slipId, recipient.teamId)
                                  }
                                >
                                  <UserIcon size={16} weight="fill" />
                                </button>
                              </div>
                            )}
                          </td>
                          {i === 0 && (
                            <td
                              rowSpan={rowCount}
                              className={cn(
                                cellClass,
                                "border-r border-b align-top",
                              )}
                            >
                              <div
                                className={cn(
                                  stickyRowHeaderContentClass,
                                  "flex h-10 items-center justify-center",
                                )}
                              >
                                <button
                                  type="button"
                                  className={iconButtonClass}
                                  title={`Voir tous les exemplaires du bordereau n° ${label}`}
                                  aria-label={`Voir tous les exemplaires du bordereau n° ${label}`}
                                  onClick={() => openBordereau(item.slipId)}
                                >
                                  <UsersThreeIcon size={18} weight="fill" />
                                </button>
                              </div>
                            </td>
                          )}
                          <td
                            className={rowCell}
                            title={doc?.authorCompanyName ?? undefined}
                          >
                            {doc?.authorCompanyName}
                          </td>
                          <td className={rowCell} title={doc?.lot ?? undefined}>
                            {doc?.lot}
                          </td>
                          <td
                            className={cn(rowCell, "text-center")}
                            title={doc?.number ?? undefined}
                          >
                            {doc?.number}
                          </td>
                          <td
                            className={rowCell}
                            title={doc?.name ?? undefined}
                          >
                            {doc?.name}
                          </td>
                          <td className={cn(rowCell, "text-center")}>
                            {doc?.index}
                          </td>
                          <td className={cn(rowCell, "text-center")}>
                            {doc?.quantity}
                          </td>
                          <td className={cn(rowCell, "text-center")}>
                            {isNotNil(pdfId) && (
                              <button
                                type="button"
                                className={iconButtonClass}
                                title={`Ouvrir le PDF de ${docName}`}
                                aria-label={`Ouvrir le PDF de ${docName}`}
                                onClick={() => openDocumentPdf(pdfId)}
                              >
                                <FilePdfIcon size={18} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
